import cn from 'classnames';
import isUndefined from 'lodash/isUndefined';
import { useTranslation } from 'react-i18next';
import { Typography } from 'react-md';
import { Collapse } from '@react-md/transition';
import { useAppSelector } from '@app/hooks';
import { PostProduction } from '@services/df/technologies';
import { FCC, PriceConfigFormFields, PriceConfigFormPostProduction } from '@types';
import { composeNodeId } from '@utils/text';
import { ColorFieldsContainer } from '@components/color-field';
import { Price } from '@components/price';
import { FeatureContent } from '@components/feature-content';
import { selectPostProductionPricingData } from '@modules/post-production';
import { priceConfigId } from '../../helpers';

import styles from './post-production.module.scss';

interface Props {
    postProduction: PostProduction;
    fieldName: PriceConfigFormFields.PostProduction;
    currentValue?: PriceConfigFormPostProduction;
    onPostProductionOptionChange: (value: PriceConfigFormPostProduction) => void;
}

export const PostProductionRow: FCC<Props> = ({
    postProduction,
    fieldName,
    currentValue,
    onPostProductionOptionChange,
    children,
}) => {
    const { id: uuid, colors, minimal_price, setup_cost, price_multiplier, minimal_within_line_item } = postProduction;
    const prices = useAppSelector(selectPostProductionPricingData) || {};
    const { t } = useTranslation();

    if (!uuid) return null;

    const pricing = prices[uuid];
    const { price } = pricing || {};
    const hasMultiplier = !!price_multiplier;
    const isSelected = !!currentValue;

    const renderPrice = () => {
        return isUndefined(price) ? null : <Price>{price}</Price>;
    };

    const minimalPrice = () =>
        minimal_price && minimal_price > price ? (
            <Typography type="subtitle-2" component={'div'} className={cn(styles.note)}>
                {minimal_within_line_item ? (
                    <FeatureContent
                        contentKey={priceConfigId('post_production', 'minimal_price_per_line_item')}
                        fallback={t('labels.minPricePerLineItem', 'Min. price per line item')}
                    />
                ) : (
                    <FeatureContent
                        contentKey={priceConfigId('post_production', 'minimal_price')}
                        fallback={t('labels.minPrice', 'Min. price')}
                    />
                )}{' '}
                <Price>{minimal_price}</Price>
            </Typography>
        ) : null;

    const setupCost = () =>
        setup_cost ? ( // this test includes setup_const !== 0
            <Typography type="subtitle-2" component={'div'} className={cn(styles.note)}>
                <FeatureContent
                    contentKey={priceConfigId('post_production', 'setup_cost')}
                    fallback={t('labels.setupCost', 'Excl. setup cost of')}
                />{' '}
                <Price>{setup_cost}</Price>
            </Typography>
        ) : null;

    return (
        <>
            {children}
            <div className={cn(styles.pricing, 'w-full')}>
                {!hasMultiplier && (
                    <>
                        {renderPrice()}
                        {minimalPrice()}
                        {setupCost()}
                    </>
                )}
            </div>

            {/* post-production options, currently only colors may exist, if others appear, put it in a component */}
            {!!colors?.length && (
                <Collapse
                    className={styles.collapse}
                    collapsed={!isSelected}
                    timeout={{
                        enter: 150,
                        exit: 150,
                    }}
                >
                    <ColorFieldsContainer
                        name={composeNodeId(fieldName, 'color', uuid)}
                        onChange={({ target: { value: colorUuid, checked } }) =>
                            onPostProductionOptionChange({
                                uuid,
                                option_uuid: colorUuid,
                            })
                        }
                        className={styles.color}
                        options={colors.map(({ id: colorUuid, label, color }) => ({
                            id: priceConfigId(fieldName, 'color_field', colorUuid),
                            value: colorUuid,
                            checked: currentValue?.option_uuid === colorUuid,
                            label,
                            color,
                        }))}
                    />
                </Collapse>
            )}
        </>
    );
};
